/**
 * `vectorStyleModel.ts` — pure helpers for the WP-4 Vector ToolCard.
 *
 * The model surface is intentionally tiny: a `VectorStyleState`
 * snapshot, a `VectorFilter` predicate, and pure deriving functions so
 * the ToolCard, the adapter style hooks, and the unit tests share one
 * source of truth. Keeping the math here (and not in the TSX) means
 * `frontend/src/components/vector/VectorToolCard.tsx` stays focused on
 * presentation.
 */
import { tokens } from '@/design';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  DEFAULT_POLYGON_SYMBOL,
  type PolygonSymbolState,
} from './polygonSymbolRegistry';
import {
  DEFAULT_LINE_SYMBOL,
  type LineSymbolState,
} from './lineSymbolRegistry';

// ---------------------------------------------------------------------------
// Token-derived swatches (Vector Card §7.4 "token swatches" requirement)
// ---------------------------------------------------------------------------

export interface VectorSwatch {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** Non-color cue (Material symbol) so the swatch is keyboard / SR friendly. */
  readonly icon:
    | 'verified'
    | 'info'
    | 'warning'
    | 'error'
    | 'public'
    | 'polyline'
    | 'pentagon'
    | 'location_on';
  /** Optional caption / micro-explanation for the swatch. */
  readonly hint?: string;
}

/**
 * Token-driven swatch list. Values resolve to the bridge layer's
 * semantic / accent tokens so component code never hard-codes hex.
 */
export const VECTOR_STROKE_SWATCHES: ReadonlyArray<VectorSwatch> = [
  {
    id: 'accent',
    label: 'Brand accent',
    value: tokens.color.accent.primary,
    icon: 'verified',
    hint: 'Default project-brand stroke.',
  },
  {
    id: 'info',
    label: 'Info',
    value: tokens.color.semantic.info,
    icon: 'info',
    hint: 'Use for advisory / reference geometry.',
  },
  {
    id: 'success',
    label: 'Success',
    value: tokens.color.semantic.success,
    icon: 'verified',
    hint: 'Use for trusted / verified data.',
  },
  {
    id: 'warning',
    label: 'Warning',
    value: tokens.color.semantic.warning,
    icon: 'warning',
    hint: 'Use for stale / requires-review data.',
  },
  {
    id: 'danger',
    label: 'Critical',
    value: tokens.color.semantic.danger,
    icon: 'error',
    hint: 'Use for blockers / conflicts only.',
  },
];

export const VECTOR_FILL_SWATCHES: ReadonlyArray<VectorSwatch> = [
  {
    id: 'accent',
    label: 'Brand accent',
    value: tokens.color.accent.primary,
    icon: 'public',
  },
  {
    id: 'info',
    label: 'Info',
    value: tokens.color.semantic.info,
    icon: 'info',
  },
  {
    id: 'success',
    label: 'Success',
    value: tokens.color.semantic.success,
    icon: 'verified',
  },
  {
    id: 'warning',
    label: 'Warning',
    value: tokens.color.semantic.warning,
    icon: 'warning',
  },
  {
    id: 'subtle',
    label: 'Subtle',
    value: tokens.color.accent.subtle,
    icon: 'public',
  },
];

/**
 * Point-icon swatches: only ligatures already present in
 * `ICON_REGISTRY` are listed so the icon picker can never emit an
 * out-of-registry name (anti-pattern A9).
 */
export type BuiltInPointSymbolId =
  | 'circle'
  | 'square'
  | 'triangle'
  | 'star'
  | 'diamond'
  | 'pin'
  | 'flag'
  | 'home'
  | 'car'
  | 'marker'
  | 'checkpoint'
  | 'destination';

/**
 * Either a built-in shape name or a namespaced icon id from
 * `pointIconRegistry` (e.g. `maki:bar`). The `string & {}` arm keeps
 * autocomplete for the built-ins while accepting the ~770 registry ids, which
 * are loaded at runtime and cannot be enumerated in a union.
 */
export type PointSymbolId = BuiltInPointSymbolId | (string & NonNullable<unknown>);

/**
 * The primitive each id renders as (and its base rotation) lives in
 * `pointSymbolShape.ts`, which both engines read — keep the mapping there
 * rather than duplicating a per-engine shape name on the swatch.
 */
export const VECTOR_ICON_SWATCHES: ReadonlyArray<{
  readonly id: BuiltInPointSymbolId;
  readonly label: string;
  readonly icon: string;
}> = [
  { id: 'circle', label: 'Circle', icon: 'circle' },
  { id: 'square', label: 'Square', icon: 'square' },
  { id: 'triangle', label: 'Triangle', icon: 'triangle' },
  { id: 'star', label: 'Star', icon: 'star' },
  { id: 'diamond', label: 'Diamond', icon: 'diamond' },
  { id: 'pin', label: 'Pin', icon: 'location_on' },
  { id: 'flag', label: 'Flag', icon: 'flag' },
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'car', label: 'Car', icon: 'directions_car' },
  { id: 'marker', label: 'Marker', icon: 'location_on' },
  { id: 'checkpoint', label: 'Checkpoint', icon: 'radio_button_checked' },
  { id: 'destination', label: 'Destination', icon: 'place' },
];

// ---------------------------------------------------------------------------
// Style + filter snapshot
// ---------------------------------------------------------------------------

export interface VectorStyleState {
  readonly primarySymbology: 'single' | 'unique-values';
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly lineSymbol: LineSymbolState;
  readonly fillColor: string;
  readonly fillOpacity: number;
  readonly polygonSymbol: PolygonSymbolState;
  readonly uniqueValueField: string | null;
  readonly uniqueValueScheme: UniqueValueColorScheme;
  readonly uniqueValueClasses: ReadonlyArray<UniqueValueClass>;
  readonly iconSwatchId: PointSymbolId;
  readonly pointSize: number;
  /** Clockwise degrees applied on top of the symbol's own base rotation. */
  readonly pointRotation: number;
  readonly labelsEnabled: boolean;
  readonly labelField: string | null;
}

export const DEFAULT_VECTOR_STYLE: VectorStyleState = {
  primarySymbology: 'single',
  strokeColor: tokens.color.accent.primary,
  strokeWidth: 2,
  lineSymbol: DEFAULT_LINE_SYMBOL,
  fillColor: tokens.color.accent.subtle,
  fillOpacity: 0.2,
  polygonSymbol: DEFAULT_POLYGON_SYMBOL,
  uniqueValueField: null,
  uniqueValueScheme: 'vivid',
  uniqueValueClasses: [],
  iconSwatchId: 'circle',
  pointSize: 16,
  pointRotation: 0,
  labelsEnabled: false,
  labelField: null,
};

export type VectorFilterOperator = 'equals' | 'contains' | 'isEmpty';

export interface VectorFilter {
  readonly attribute: string | null;
  readonly operator: VectorFilterOperator;
  readonly value: string;
}

export const DEFAULT_VECTOR_FILTER: VectorFilter = {
  attribute: null,
  operator: 'equals',
  value: '',
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function resolveSwatch(
  swatches: ReadonlyArray<VectorSwatch>,
  id: string,
): VectorSwatch {
  return (
    swatches.find((swatch) => swatch.id === id) ?? swatches[0] ?? VECTOR_STROKE_SWATCHES[0]
  );
}

export interface MaplibreVectorPaintDelta {
  readonly fillColor?: string;
  readonly fillOpacity?: number;
  readonly lineColor?: string;
  readonly lineWidth?: number;
  readonly lineSymbol?: LineSymbolState;
  readonly pointSize?: number;
  readonly pointSymbolId?: PointSymbolId;
  readonly pointRotation?: number;
  readonly polygonSymbol?: PolygonSymbolState;
  readonly uniqueValueSymbology?: UniqueValueSymbology | null;
}

/**
 * Translate a `VectorStyleState` into the paint-property delta that the
 * MapLibre adapter `setVectorPaint` consumes.
 */
export function vectorStyleToPaintDelta(
  state: VectorStyleState,
): MaplibreVectorPaintDelta {
  return {
    lineColor: state.strokeColor,
    lineWidth: state.strokeWidth,
    lineSymbol: {
      ...state.lineSymbol,
      color: state.strokeColor,
      width: state.strokeWidth,
    },
    fillColor: state.fillColor,
    fillOpacity: state.fillOpacity,
    pointSize: state.pointSize,
    pointSymbolId: state.iconSwatchId,
    pointRotation: state.pointRotation,
    polygonSymbol: {
      ...state.polygonSymbol,
      backgroundColor: state.fillColor,
    },
    uniqueValueSymbology:
      state.primarySymbology === 'unique-values' && state.uniqueValueField
        ? {
            field: state.uniqueValueField,
            classes: state.uniqueValueClasses,
          }
        : null,
  };
}

export type UniqueValue = string | number | boolean | null;
export type UniqueValueColorScheme = 'vivid' | 'pastel' | 'earth';

export interface UniqueValueClass {
  readonly id: string;
  readonly value: UniqueValue;
  readonly label: string;
  readonly visible: boolean;
  readonly fillColor: string;
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly lineSymbol: LineSymbolState;
  readonly fillOpacity: number;
  readonly polygonSymbol: PolygonSymbolState;
}

export interface UniqueValueSymbology {
  readonly field: string;
  readonly classes: ReadonlyArray<UniqueValueClass>;
}

export function buildUniqueValueClasses(
  features: ReadonlyArray<{ readonly properties?: Readonly<Record<string, unknown>> }>,
  field: string,
  scheme: UniqueValueColorScheme,
  previous: ReadonlyArray<UniqueValueClass> = [],
): ReadonlyArray<UniqueValueClass> {
  const seen = new Map<string, UniqueValue>();
  for (const feature of features) {
    const value = normalizeUniqueValue(feature.properties?.[field]);
    seen.set(uniqueValueKey(value), value);
  }
  const previousByKey = new Map(previous.map((entry) => [uniqueValueKey(entry.value), entry]));
  return [...seen.values()]
    .sort((left, right) => String(left ?? '').localeCompare(String(right ?? ''), undefined, {
      numeric: true,
    }))
    .slice(0, 100)
    .map((value, index) => {
      const existing = previousByKey.get(uniqueValueKey(value));
      if (existing) return existing;
      const fillColor = uniqueValueColor(index, scheme);
      return {
        id: `${field}-${uniqueValueKey(value)}`,
        value,
        label: value == null ? '<Null>' : String(value),
        visible: true,
        fillColor,
        strokeColor: fillColor,
        strokeWidth: DEFAULT_VECTOR_STYLE.strokeWidth,
        lineSymbol: {
          ...DEFAULT_LINE_SYMBOL,
          color: fillColor,
        },
        fillOpacity: 0.7,
        polygonSymbol: {
          ...DEFAULT_POLYGON_SYMBOL,
          backgroundColor: fillColor,
        },
      };
    });
}

export function uniqueValueKey(value: UniqueValue): string {
  if (value === null) return 'null';
  return `${typeof value}:${String(value)}`;
}

export function uniqueValueColor(
  index: number,
  scheme: UniqueValueColorScheme,
): string {
  const hue = (index * 137.508 + (scheme === 'earth' ? 25 : 0)) % 360;
  const saturation = scheme === 'pastel' ? 58 : scheme === 'earth' ? 42 : 72;
  const lightness = scheme === 'pastel' ? 72 : scheme === 'earth' ? 48 : 52;
  return hslToHex(hue, saturation, lightness);
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = l - chroma / 2;
  const [red, green, blue] =
    hue < 60 ? [chroma, x, 0]
      : hue < 120 ? [x, chroma, 0]
        : hue < 180 ? [0, chroma, x]
          : hue < 240 ? [0, x, chroma]
            : hue < 300 ? [x, 0, chroma]
              : [chroma, 0, x];
  return `#${[red, green, blue]
    .map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

function normalizeUniqueValue(value: unknown): UniqueValue {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return String(value);
}

export function vectorStyleFromLayer(
  layer: Layer | null | undefined,
  override?: VectorStyleState | null,
): VectorStyleState {
  if (override) {
    return {
      ...DEFAULT_VECTOR_STYLE,
      ...override,
      lineSymbol: {
        ...DEFAULT_LINE_SYMBOL,
        ...(override.lineSymbol ?? {}),
        color: override.lineSymbol?.color ?? override.strokeColor,
        width: override.lineSymbol?.width ?? override.strokeWidth,
      },
      uniqueValueClasses: override.uniqueValueClasses.map((entry) => ({
        ...entry,
        lineSymbol: {
          ...DEFAULT_LINE_SYMBOL,
          ...(entry.lineSymbol ?? {}),
          color: entry.lineSymbol?.color ?? entry.strokeColor,
          width: entry.lineSymbol?.width ?? entry.strokeWidth,
        },
      })),
    };
  }
  const source = layer?.style ?? {};
  const strokeColor = typeof source.strokeColor === 'string'
    ? source.strokeColor
    : typeof source.color === 'string'
      ? source.color
      : DEFAULT_VECTOR_STYLE.strokeColor;
  const fillColor = typeof source.fillColor === 'string'
    ? source.fillColor
    : typeof source.color === 'string'
      ? source.color
      : DEFAULT_VECTOR_STYLE.fillColor;
  return {
    ...DEFAULT_VECTOR_STYLE,
    strokeColor,
    fillColor,
    strokeWidth: finiteOr(source.strokeWidth ?? source.lineWidth, DEFAULT_VECTOR_STYLE.strokeWidth),
    lineSymbol: {
      ...DEFAULT_LINE_SYMBOL,
      color: strokeColor,
      width: finiteOr(source.strokeWidth ?? source.lineWidth, DEFAULT_VECTOR_STYLE.strokeWidth),
      opacity: clamp01(finiteOr(source.lineOpacity, DEFAULT_LINE_SYMBOL.opacity)),
    },
    fillOpacity: clamp01(finiteOr(source.fillOpacity, DEFAULT_VECTOR_STYLE.fillOpacity)),
    polygonSymbol: {
      ...DEFAULT_POLYGON_SYMBOL,
      backgroundColor: fillColor,
    },
  };
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Apply a (simple) `VectorFilter` to a feature property bag. Pure so
 * tests can pin the predicate semantics without React in the loop.
 */
export function featureMatchesFilter(
  properties: Readonly<Record<string, unknown>>,
  filter: VectorFilter,
): boolean {
  if (!filter.attribute) return true;
  const raw = properties[filter.attribute];
  switch (filter.operator) {
    case 'isEmpty':
      return raw === null || raw === undefined || String(raw).trim().length === 0;
    case 'contains':
      return typeof raw === 'string'
        ? raw.toLowerCase().includes(filter.value.trim().toLowerCase())
        : String(raw ?? '').toLowerCase().includes(filter.value.trim().toLowerCase());
    case 'equals':
    default: {
      if (raw === null || raw === undefined) return filter.value.trim().length === 0;
      if (typeof raw === 'number') return Number(filter.value) === raw;
      return String(raw) === filter.value;
    }
  }
}

/**
 * Build a MapLibre filter expression (`['==', ['get', attr], value]`)
 * for the equality / contains / empty cases. Returns `null` when the
 * filter is "all".
 */
export type MaplibreFilterSpec = readonly [string, ...unknown[]] | null;

export function vectorFilterToMaplibre(filter: VectorFilter): MaplibreFilterSpec {
  if (!filter.attribute) return null;
  switch (filter.operator) {
    case 'isEmpty':
      return ['any', ['==', ['get', filter.attribute], ''], ['!', ['has', filter.attribute]]];
    case 'contains':
      return [
        'in',
        filter.value,
        ['to-string', ['get', filter.attribute]],
      ];
    case 'equals':
    default:
      return ['==', ['get', filter.attribute], filter.value];
  }
}

// ---------------------------------------------------------------------------
// OGC / source info
// ---------------------------------------------------------------------------

export interface VectorSourceInfo {
  readonly kind: 'geojson' | 'pmtiles' | 'ogc-features' | 'flatgeobuf' | 'geoparquet' | 'unknown';
  readonly endpoint: string | null;
  readonly featureCount?: number | null;
  readonly supportedCrs?: ReadonlyArray<string>;
  readonly capabilities?: ReadonlyArray<string>;
}

export function deriveVectorSourceInfo(layer: Layer | null): VectorSourceInfo {
  if (!layer) {
    return { kind: 'unknown', endpoint: null };
  }
  const endpoints = layer.endpoints ?? {};
  if (endpoints.features) {
    return {
      kind: 'ogc-features',
      endpoint: endpoints.features,
      supportedCrs: [layer.crs],
      capabilities: ['items', 'collections', 'conformance'],
    };
  }
  if (endpoints.pmtiles) {
    return {
      kind: 'pmtiles',
      endpoint: endpoints.pmtiles,
      supportedCrs: [layer.crs],
      capabilities: ['range-read', 'mvt'],
    };
  }
  const style = layer.style ?? {};
  const declared = typeof style['format'] === 'string' ? style['format'] : null;
  switch (declared) {
    case 'flatgeobuf':
      return { kind: 'flatgeobuf', endpoint: null, supportedCrs: [layer.crs] };
    case 'geoparquet':
      return { kind: 'geoparquet', endpoint: null, supportedCrs: [layer.crs] };
    default:
      return { kind: 'geojson', endpoint: endpoints.tiles ?? null, supportedCrs: [layer.crs] };
  }
}

// ---------------------------------------------------------------------------
// Tool-card-kind gate (mirrors `getToolCardKindFor` for safety)
// ---------------------------------------------------------------------------

const VECTOR_TOOLCARD_LAYER_KINDS: ReadonlySet<string> = new Set([
  'vector',
  'pmtiles',
  'geojson',
  'flatgeobuf',
  'geoparquet',
  'dxf',
  'ogc-features-snapshot',
  'featureLayer',
]);

export function isVectorToolCardLayer(layer: Layer | null | undefined): boolean {
  if (!layer) return false;
  return VECTOR_TOOLCARD_LAYER_KINDS.has(layer.kind);
}
